import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { resolveNewsGoogleReconsentCompleteUrl } from "../lib/config";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  clearPendingNewsGoogleReconsentSnapshot,
  normalizeNewsGoogleAuthorizationUrl,
  readPendingNewsGoogleReconsentSnapshot,
  writePendingNewsGoogleReconsentSnapshot,
  type PendingNewsGoogleReconsentSnapshot,
} from "../lib/newsGoogleReconsent";
import {
  createPendingNewsJobSnapshot,
  type PendingNewsJobSnapshot,
  writePendingNewsJobSnapshot,
} from "../lib/newsJob";
import { resolveNewsJobConflictDisplay } from "../lib/newsJobError";
import { storage } from "../lib/storage";

type NewsReconsentPhase = "checking" | "resubmitting" | "error";

function getPhaseMessage(phase: NewsReconsentPhase) {
  if (phase === "checking") {
    return "Google 連携の状態を確認しています...";
  }

  if (phase === "resubmitting") {
    return "ニュース配信を再送しています...";
  }

  return "再確認が必要です。";
}

export default function NewsGoogleReconsentComplete() {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<PendingNewsGoogleReconsentSnapshot | null>(() =>
    readPendingNewsGoogleReconsentSnapshot()
  );
  const [phase, setPhase] = useState<NewsReconsentPhase>("checking");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openAuthorizationUrl = useCallback(() => {
    if (!snapshot?.authorizationUrl) return;
    const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(snapshot.authorizationUrl);
    if (!authorizationUrl) {
      clearPendingNewsGoogleReconsentSnapshot();
      setSnapshot(null);
      setPhase("error");
      setErrorMessage("Google連携先URLが不正です。チャット画面から再度お試しください。");
      return;
    }

    const nextSnapshot = {
      ...snapshot,
      authorizationUrl,
      redirectUrl: snapshot.redirectUrl || resolveNewsGoogleReconsentCompleteUrl(),
    };

    writePendingNewsGoogleReconsentSnapshot(nextSnapshot);
    window.location.assign(authorizationUrl);
  }, [snapshot]);

  const runStatusCheckAndResubmit = useCallback(async () => {
    const currentSnapshot = readPendingNewsGoogleReconsentSnapshot();
    setSnapshot(currentSnapshot);

    if (!currentSnapshot) {
      setPhase("error");
      setErrorMessage("追加同意情報が見つかりませんでした。チャット画面からもう一度お試しください。");
      return;
    }

    const currentUserId = storage.getAuth().userId?.trim() ?? "";
    if (!currentUserId || currentUserId !== currentSnapshot.wizmateUserId) {
      clearPendingNewsGoogleReconsentSnapshot();
      setSnapshot(null);
      setPhase("error");
      setErrorMessage("ログインユーザーが変更されたため、再連携情報を破棄しました。チャット画面から再度お試しください。");
      return;
    }

    setErrorMessage(null);
    setPhase("checking");

    try {
      const status = await api.getGoogleIntegrationStatusByUserScope({
        wizmateUserId: currentSnapshot.wizmateUserId,
        scopeBundle: currentSnapshot.scopeBundle,
      });
      const isGranted =
        status.scopeBundleGranted === true &&
        Array.isArray(status.missingScopes) &&
        status.missingScopes.length === 0;

      if (!isGranted) {
        setPhase("error");
        setErrorMessage("Google 連携がまだ完了していません。再度連携してください。");
        return;
      }

      setPhase("resubmitting");

      const isReport = currentSnapshot.mode === "report";
      const response = await api.createNewsJob({
        keyword: currentSnapshot.keyword,
        notificationType: "email",
        email: currentSnapshot.email,
        redirectUrl: currentSnapshot.redirectUrl,
        mode: currentSnapshot.mode,
        ...(!isReport && currentSnapshot.readingProfile
          ? { readingProfile: currentSnapshot.readingProfile }
          : {}),
        ...(currentSnapshot.deliveryOption
          ? { deliveryOption: currentSnapshot.deliveryOption }
          : {}),
      });

      storage.setNewsEmail(currentSnapshot.email);
      const pendingSnapshot: PendingNewsJobSnapshot = {
        ...createPendingNewsJobSnapshot(response),
        mode: currentSnapshot.mode ?? null,
        readingProfile: isReport ? null : (currentSnapshot.readingProfile ?? null),
        deliveryOption: currentSnapshot.deliveryOption ?? null,
      };
      writePendingNewsJobSnapshot(pendingSnapshot);
      clearPendingNewsGoogleReconsentSnapshot();

      navigate("/chat", {
        replace: true,
        state: {
          reopenNewsJobModal: true,
          newsJobForm: {
            keyword: currentSnapshot.keyword,
            notificationType: "email",
            email: currentSnapshot.email,
            mode: currentSnapshot.mode,
            readingProfile: currentSnapshot.readingProfile,
            deliveryOption: currentSnapshot.deliveryOption,
          },
        },
      });
    } catch (error) {
      logError(error);

      if (error instanceof AppError && error.code === "CONFLICT") {
        const conflictDisplay = resolveNewsJobConflictDisplay(error);

        if (conflictDisplay?.connectAuthorizationUrl && currentSnapshot) {
          const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(
            conflictDisplay.connectAuthorizationUrl
          );
          if (!authorizationUrl) {
            clearPendingNewsGoogleReconsentSnapshot();
            setSnapshot(null);
            setPhase("error");
            setErrorMessage("Google連携先URLが不正です。チャット画面から再度お試しください。");
            return;
          }

          const nextSnapshot = {
            ...currentSnapshot,
            authorizationUrl,
            scopeBundle:
              conflictDisplay.requiredScopeBundle ?? currentSnapshot.scopeBundle,
            redirectUrl:
              currentSnapshot.redirectUrl || resolveNewsGoogleReconsentCompleteUrl(),
          };
          writePendingNewsGoogleReconsentSnapshot(nextSnapshot);
          setSnapshot(nextSnapshot);
        }

        if (conflictDisplay?.shouldShowGoogleReconnect) {
          setPhase("error");
          setErrorMessage("Gmail 配信には Google連携の追加同意が必要です。");
          return;
        }
      }

      setPhase("error");
      setErrorMessage(toUserMessage(error));
    }
  }, [navigate]);

  useEffect(() => {
    void runStatusCheckAndResubmit();
  }, [runStatusCheckAndResubmit]);

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-6 py-10 text-[#4A3E39]">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center justify-center">
        <div className="w-full rounded-[2rem] border border-white/80 bg-white/90 p-6 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm">
          <p className="text-[12px] font-black uppercase tracking-[0.18em] text-[#A67C52]">
            News Delivery
          </p>
          <h1 className="mt-2 text-2xl font-black text-[#7C5A4E]">
            Gmail 追加同意の確認
          </h1>
          <p className="mt-3 text-sm leading-6 text-[#6B5851]">{getPhaseMessage(phase)}</p>

          {snapshot ? (
            <dl className="mt-4 space-y-2 rounded-[1.2rem] border border-[#E4D5C8] bg-[#FFF8F4] px-4 py-3 text-sm text-[#6A4F44]">
              <div className="flex items-start justify-between gap-4">
                <dt className="font-bold">キーワード</dt>
                <dd className="break-all text-right">{snapshot.keyword}</dd>
              </div>
              <div className="flex items-start justify-between gap-4">
                <dt className="font-bold">配信先</dt>
                <dd className="break-all text-right">{snapshot.email}</dd>
              </div>
            </dl>
          ) : null}

          {errorMessage ? (
            <div className="mt-4 rounded-[1.2rem] border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              {errorMessage}
            </div>
          ) : null}

          <div className="mt-5 flex flex-col gap-3">
            {phase === "error" ? (
              <button
                type="button"
                onClick={() => {
                  void runStatusCheckAndResubmit();
                }}
                className="inline-flex items-center justify-center rounded-full bg-[#6A4F44] px-5 py-3 text-sm font-black text-white transition hover:bg-[#563E35]"
              >
                再確認する
              </button>
            ) : null}

            {snapshot?.authorizationUrl ? (
              <button
                type="button"
                onClick={openAuthorizationUrl}
                className="inline-flex items-center justify-center rounded-full bg-[#B07A55] px-5 py-3 text-sm font-black text-white transition hover:bg-[#986648]"
              >
                追加同意する
              </button>
            ) : null}

            <button
              type="button"
              onClick={() => navigate("/chat", { replace: true })}
              className="inline-flex items-center justify-center rounded-full border border-[#E4D5C8] bg-white px-5 py-3 text-sm font-bold text-[#6A4F44] transition hover:bg-[#FFF8F1]"
            >
              チャットに戻る
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
